import React, { useState, Fragment } from 'react';
import { Sidebar } from '../../components/shared/Sidebar';
import { Breadcrumbs } from '../../components/shared/Breadcrumbs';
import { motion } from 'framer-motion';
import { Code, Database, GitBranch, Shield, Bell, CreditCard, Users, Building, Activity, Lock, Zap, FileText, CheckCircle, AlertCircle, ArrowRight, ChevronRight } from 'lucide-react';
const sections = [{
  id: 'overview',
  title: 'System Overview',
  icon: Code
}, {
  id: 'architecture',
  title: 'Architecture',
  icon: GitBranch
}, {
  id: 'tenant-flow',
  title: 'Tenant Flow',
  icon: Users
}, {
  id: 'landlord-flow',
  title: 'Landlord Flow',
  icon: Building
}, {
  id: 'admin-flow',
  title: 'Admin Flow',
  icon: Shield
}, {
  id: 'payment-flow',
  title: 'Payment Flow',
  icon: CreditCard
}, {
  id: 'notifications',
  title: 'Notifications',
  icon: Bell
}, {
  id: 'security',
  title: 'Security & Compliance',
  icon: Lock
}, {
  id: 'tech-stack',
  title: 'Tech Stack',
  icon: Database
}, {
  id: 'api-reference',
  title: 'API Reference',
  icon: FileText
}];
export function DeveloperDocs() {
  const [activeSection, setActiveSection] = useState('overview');
  const renderContent = () => {
    switch (activeSection) {
      case 'overview':
        return <div className="space-y-6" data-id="element-4227">
            <div data-id="element-4228">
              <h2 className="text-3xl font-bold text-gray-900 mb-4" data-id="element-4229">
                Renta System Overview
              </h2>
              <p className="text-lg text-gray-600 leading-relaxed" data-id="element-4230">
                Renta is a trust-first rent settlement and monthly recovery
                platform designed for the Nigerian rental market.
              </p>
            </div>

            <div className="bg-blue-50 border border-blue-200 rounded-xl p-6" data-id="element-4231">
              <h3 className="text-lg font-semibold text-blue-900 mb-3 flex items-center" data-id="element-4232">
                <Zap className="h-5 w-5 mr-2" data-id="element-4233" />
                Core Concept
              </h3>
              <ul className="space-y-2 text-blue-800" data-id="element-4234">
                <li className="flex items-start" data-id="element-4235">
                  <CheckCircle className="h-5 w-5 mr-2 mt-0.5 flex-shrink-0" data-id="element-4236" />
                  <span data-id="element-4237">
                    Tenants pay monthly, landlords receive annual/upfront
                    payments
                  </span>
                </li>
                <li className="flex items-start" data-id="element-4238">
                  <CheckCircle className="h-5 w-5 mr-2 mt-0.5 flex-shrink-0" data-id="element-4239" />
                  <span data-id="element-4240">
                    Renta fronts the rent and recovers monthly from tenants
                  </span>
                </li>
                <li className="flex items-start" data-id="element-4241">
                  <CheckCircle className="h-5 w-5 mr-2 mt-0.5 flex-shrink-0" data-id="element-4242" />
                  <span data-id="element-4243">Trust-first, mobile-optimized, operationally lean</span>
                </li>
              </ul>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6" data-id="element-4244">
              <div className="bg-white border border-gray-200 rounded-xl p-6" data-id="element-4245">
                <h3 className="text-lg font-semibold text-gray-900 mb-4" data-id="element-4246">
                  System Modules
                </h3>
                <div className="space-y-3" data-id="element-4247">
                  {[{
                  name: 'Tenant Module',
                  desc: 'Onboarding, verification, payments'
                }, {
                  name: 'Landlord Module',
                  desc: 'Property management, settlements'
                }, {
                  name: 'Admin Module',
                  desc: 'Workflow management, analytics'
                }, {
                  name: 'Payment Engine',
                  desc: 'Direct debit, reconciliation'
                }, {
                  name: 'Notifications',
                  desc: 'SMS, email, push alerts'
                }, {
                  name: 'Risk & Compliance',
                  desc: 'Scoring, audit trails, NDPR'
                }].map(module => <div key={module.name} className="flex items-start" data-id="element-4248">
                      <ArrowRight className="h-4 w-4 text-blue-600 mr-2 mt-1 flex-shrink-0" data-id="element-4249" />
                      <div data-id="element-4250">
                        <p className="text-sm font-medium text-gray-900" data-id="element-4251">
                          {module.name}
                        </p>
                        <p className="text-xs text-gray-500" data-id="element-4252">{module.desc}</p>
                      </div>
                    </div>)}
                </div>
              </div>

              <div className="bg-white border border-gray-200 rounded-xl p-6" data-id="element-4253">
                <h3 className="text-lg font-semibold text-gray-900 mb-4" data-id="element-4254">
                  Key Features
                </h3>
                <div className="space-y-3" data-id="element-4255">
                  {['AI-powered risk scoring', 'Payroll integration (SeamlessHR, Paystack)', 'Direct debit automation', 'Real-time notifications', 'Multi-role dashboards', 'Document verification', 'Settlement engine', 'Analytics & reporting'].map(feature => <div key={feature} className="flex items-center text-sm text-gray-700" data-id="element-4256">
                      <CheckCircle className="h-4 w-4 text-green-500 mr-2 flex-shrink-0" data-id="element-4257" />
                      {feature}
                    </div>)}
                </div>
              </div>
            </div>
          </div>;
      case 'architecture':
        return <div className="space-y-6" data-id="element-4258">
            <div data-id="element-4259">
              <h2 className="text-3xl font-bold text-gray-900 mb-4" data-id="element-4260">
                System Architecture
              </h2>
              <p className="text-gray-600" data-id="element-4261">
                Microservices-based architecture for scalability and
                maintainability.
              </p>
            </div>

            <div className="bg-gray-900 text-green-400 rounded-xl p-6 font-mono text-sm overflow-x-auto" data-id="element-4262">
              <pre data-id="element-4263">{`[Frontend] → React Web / React Native Mobile
     |
     v
[Backend API] → Node.js / Django / Laravel
     |
     +--> [Database] PostgreSQL + MongoDB
     |
     +--> [Payments] Remita / Paystack / NIBSS
     |
     +--> [Bank Verification] Mono / Okra API
     |
     +--> [Notifications] Twilio (SMS), Firebase (Push)
     |
     +--> [Admin Dashboard + Analytics]
     |
     +--> [Audit Trail & Compliance]`}</pre>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-6" data-id="element-4264">
              <div className="bg-white border border-gray-200 rounded-xl p-6" data-id="element-4265">
                <Database className="h-8 w-8 text-blue-600 mb-4" data-id="element-4266" />
                <h3 className="text-lg font-semibold text-gray-900 mb-2" data-id="element-4267">
                  Data Layer
                </h3>
                <ul className="text-sm text-gray-600 space-y-2" data-id="element-4268">
                  <li data-id="element-4269">• PostgreSQL: Structured data</li>
                  <li data-id="element-4270">• MongoDB: Documents</li>
                  <li data-id="element-4271">• Redis: Caching</li>
                  <li data-id="element-4272">• S3: File storage</li>
                </ul>
              </div>

              <div className="bg-white border border-gray-200 rounded-xl p-6" data-id="element-4273">
                <Zap className="h-8 w-8 text-yellow-600 mb-4" data-id="element-4274" />
                <h3 className="text-lg font-semibold text-gray-900 mb-2" data-id="element-4275">
                  Services
                </h3>
                <ul className="text-sm text-gray-600 space-y-2" data-id="element-4276">
                  <li data-id="element-4277">• Tenant Service</li>
                  <li data-id="element-4278">• Landlord Service</li>
                  <li data-id="element-4279">• Payment Service</li>
                  <li data-id="element-4280">• Notification Service</li>
                  <li data-id="element-4281">• Analytics Service</li>
                </ul>
              </div>

              <div className="bg-white border border-gray-200 rounded-xl p-6" data-id="element-4282">
                <Lock className="h-8 w-8 text-red-600 mb-4" data-id="element-4283" />
                <h3 className="text-lg font-semibold text-gray-900 mb-2" data-id="element-4284">
                  Security
                </h3>
                <ul className="text-sm text-gray-600 space-y-2" data-id="element-4285">
                  <li data-id="element-4286">• JWT / OAuth2</li>
                  <li data-id="element-4287">• Encryption at rest</li>
                  <li data-id="element-4288">• PCI DSS compliant</li>
                  <li data-id="element-4289">• NDPR compliant</li>
                  <li data-id="element-4290">• 2FA / OTP</li>
                </ul>
              </div>
            </div>
          </div>;
      case 'tenant-flow':
        return <div className="space-y-6" data-id="element-4291">
            <div data-id="element-4292">
              <h2 className="text-3xl font-bold text-gray-900 mb-4" data-id="element-4293">
                Tenant Flow (End-to-End)
              </h2>
              <p className="text-gray-600" data-id="element-4294">
                Complete journey from signup to monthly payments.
              </p>
            </div>

            <div className="space-y-4" data-id="element-4295">
              {[{
              step: '1',
              title: 'Sign-up & Authentication',
              details: ['Email/phone input → OTP verification', 'Optional 2FA setup', 'Secure credential storage']
            }, {
              step: '2',
              title: 'Profile Setup',
              details: ['Personal info: name, DOB, BVN', 'Upload ID (passport, license, NIN)', 'Upload tenancy agreement', 'Upload bank statements']
            }, {
              step: '3',
              title: 'Income Verification & Risk Scoring',
              details: ['Parse bank statement via Mono/Okra', 'Extract salary deposits and expenses', 'Compute rent-to-income ratio (max 35-40%)', 'AI risk score calculation']
            }, {
              step: '4',
              title: 'Agreement Signing',
              details: ['Digital signature on Rent Settlement Agreement', 'Secure document storage', 'Legal compliance checks']
            }, {
              step: '5',
              title: 'Payment Setup',
              details: ['Link salary account (Mono/Okra/NIBSS)', 'Standing order / auto-debit mandate', 'Schedule based on payment capacity']
            }, {
              step: '6',
              title: 'Dashboard & Ongoing',
              details: ['View payment schedule and history', 'Track coverage period', 'Receive notifications', 'Access support']
            }].map(item => <motion.div key={item.step} initial={{
              opacity: 0,
              x: -20
            }} animate={{
              opacity: 1,
              x: 0
            }} transition={{
              delay: parseInt(item.step) * 0.1
            }} className="bg-white border border-gray-200 rounded-xl p-6" data-id="element-4296">
                  <div className="flex items-start" data-id="element-4297">
                    <div className="flex-shrink-0 h-10 w-10 bg-blue-600 text-white rounded-full flex items-center justify-center font-bold text-lg mr-4" data-id="element-4298">
                      {item.step}
                    </div>
                    <div className="flex-1" data-id="element-4299">
                      <h3 className="text-lg font-semibold text-gray-900 mb-3" data-id="element-4300">
                        {item.title}
                      </h3>
                      <ul className="space-y-2" data-id="element-4301">
                        {item.details.map((detail, idx) => <li key={idx} className="flex items-start text-sm text-gray-600" data-id="element-4302">
                            <ChevronRight className="h-4 w-4 text-blue-600 mr-2 mt-0.5 flex-shrink-0" data-id="element-4303" />
                            {detail}
                          </li>)}
                      </ul>
                    </div>
                  </div>
                </motion.div>)}
            </div>
          </div>;
      case 'payment-flow':
        return <div className="space-y-6" data-id="element-4304">
            <div data-id="element-4305">
              <h2 className="text-3xl font-bold text-gray-900 mb-4" data-id="element-4306">
                Payment Flow
              </h2>
              <p className="text-gray-600" data-id="element-4307">
                Automated collection and settlement process.
              </p>
            </div>

            <div className="bg-gradient-to-r from-blue-50 to-green-50 border border-blue-200 rounded-xl p-8" data-id="element-4308">
              <h3 className="text-lg font-semibold text-gray-900 mb-6 text-center" data-id="element-4309">
                Payment Journey
              </h3>
              <div className="flex flex-col md:flex-row items-center justify-between space-y-4 md:space-y-0 md:space-x-4" data-id="element-4310">
                {[{
                label: 'Tenant Bank',
                icon: Users
              }, {
                label: 'Direct Debit',
                icon: ArrowRight
              }, {
                label: 'Renta Collection',
                icon: CreditCard
              }, {
                label: 'Renta Ledger',
                icon: Database
              }, {
                label: 'Landlord Settlement',
                icon: Building
              }].map((step, idx) => <Fragment key={idx} data-id="element-4311">
                    <div className="flex flex-col items-center" data-id="element-4312">
                      <div className="h-16 w-16 bg-white rounded-full shadow-md flex items-center justify-center mb-2" data-id="element-4313">
                        <step.icon className="h-8 w-8 text-blue-600" data-id="element-4314" />
                      </div>
                      <p className="text-sm font-medium text-gray-700 text-center" data-id="element-4315">
                        {step.label}
                      </p>
                    </div>
                    {idx < 4 && <ArrowRight className="hidden md:block h-6 w-6 text-gray-400" data-id="element-4316" />}
                  </Fragment>)}
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6" data-id="element-4317">
              <div className="bg-white border border-gray-200 rounded-xl p-6" data-id="element-4318">
                <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center" data-id="element-4319">
                  <CheckCircle className="h-5 w-5 text-green-500 mr-2" data-id="element-4320" />
                  Success Flow
                </h3>
                <ol className="space-y-3 text-sm text-gray-600" data-id="element-4321">
                  <li className="flex items-start" data-id="element-4322">
                    <span className="font-semibold text-gray-900 mr-2" data-id="element-4323">1.</span>
                    Payment auto-debited on salary date
                  </li>
                  <li className="flex items-start" data-id="element-4324">
                    <span className="font-semibold text-gray-900 mr-2" data-id="element-4325">2.</span>
                    Mark in ledger, update dashboards
                  </li>
                  <li className="flex items-start" data-id="element-4326">
                    <span className="font-semibold text-gray-900 mr-2" data-id="element-4327">3.</span>
                    Notify tenant & landlord
                  </li>
                  <li className="flex items-start" data-id="element-4328">
                    <span className="font-semibold text-gray-900 mr-2" data-id="element-4329">4.</span>
                    Update analytics and reports
                  </li>
                </ol>
              </div>

              <div className="bg-white border border-gray-200 rounded-xl p-6" data-id="element-4330">
                <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center" data-id="element-4331">
                  <AlertCircle className="h-5 w-5 text-red-500 mr-2" data-id="element-4332" />
                  Failure Handling
                </h3>
                <ol className="space-y-3 text-sm text-gray-600" data-id="element-4333">
                  <li className="flex items-start" data-id="element-4334">
                    <span className="font-semibold text-gray-900 mr-2" data-id="element-4335">1.</span>
                    Retry payment after 24-48 hours
                  </li>
                  <li className="flex items-start" data-id="element-4336">
                    <span className="font-semibold text-gray-900 mr-2" data-id="element-4337">2.</span>
                    Notify tenant + admin immediately
                  </li>
                  <li className="flex items-start" data-id="element-4338">
                    <span className="font-semibold text-gray-900 mr-2" data-id="element-4339">3.</span>
                    Flag for human intervention
                  </li>
                  <li className="flex items-start" data-id="element-4340">
                    <span className="font-semibold text-gray-900 mr-2" data-id="element-4341">4.</span>
                    Optional: Temporary coverage pause
                  </li>
                </ol>
              </div>
            </div>
          </div>;
      case 'api-reference':
        return <div className="space-y-6" data-id="element-4342">
            <div data-id="element-4343">
              <h2 className="text-3xl font-bold text-gray-900 mb-4" data-id="element-4344">
                API Reference
              </h2>
              <p className="text-gray-600" data-id="element-4345">
                RESTful API endpoints for all platform operations.
              </p>
            </div>

            <div className="bg-white border border-gray-200 rounded-xl overflow-hidden" data-id="element-4346">
              <div className="bg-gray-50 px-6 py-4 border-b border-gray-200" data-id="element-4347">
                <h3 className="text-lg font-semibold text-gray-900" data-id="element-4348">
                  Authentication
                </h3>
              </div>
              <div className="p-6 space-y-4" data-id="element-4349">
                <div className="font-mono text-sm" data-id="element-4350">
                  <div className="flex items-center mb-2" data-id="element-4351">
                    <span className="px-2 py-1 bg-green-100 text-green-800 rounded font-semibold mr-3" data-id="element-4352">
                      POST
                    </span>
                    <span className="text-gray-700" data-id="element-4353">/api/auth/register</span>
                  </div>
                  <p className="text-gray-500 text-xs ml-16" data-id="element-4354">
                    Register new user (tenant/landlord)
                  </p>
                </div>

                <div className="font-mono text-sm" data-id="element-4355">
                  <div className="flex items-center mb-2" data-id="element-4356">
                    <span className="px-2 py-1 bg-green-100 text-green-800 rounded font-semibold mr-3" data-id="element-4357">
                      POST
                    </span>
                    <span className="text-gray-700" data-id="element-4358">/api/auth/login</span>
                  </div>
                  <p className="text-gray-500 text-xs ml-16" data-id="element-4359">
                    Login with email/phone + OTP
                  </p>
                </div>

                <div className="font-mono text-sm" data-id="element-4360">
                  <div className="flex items-center mb-2" data-id="element-4361">
                    <span className="px-2 py-1 bg-green-100 text-green-800 rounded font-semibold mr-3" data-id="element-4362">
                      POST
                    </span>
                    <span className="text-gray-700" data-id="element-4363">/api/auth/verify-otp</span>
                  </div>
                  <p className="text-gray-500 text-xs ml-16" data-id="element-4364">Verify OTP code</p>
                </div>
              </div>
            </div>

            <div className="bg-white border border-gray-200 rounded-xl overflow-hidden" data-id="element-4365">
              <div className="bg-gray-50 px-6 py-4 border-b border-gray-200" data-id="element-4366">
                <h3 className="text-lg font-semibold text-gray-900" data-id="element-4367">
                  Tenant Endpoints
                </h3>
              </div>
              <div className="p-6 space-y-4" data-id="element-4368">
                <div className="font-mono text-sm" data-id="element-4369">
                  <div className="flex items-center mb-2" data-id="element-4370">
                    <span className="px-2 py-1 bg-blue-100 text-blue-800 rounded font-semibold mr-3" data-id="element-4371">
                      GET
                    </span>
                    <span className="text-gray-700" data-id="element-4372">/api/tenant/profile</span>
                  </div>
                  <p className="text-gray-500 text-xs ml-16" data-id="element-4373">
                    Get tenant profile and verification status
                  </p>
                </div>

                <div className="font-mono text-sm" data-id="element-4374">
                  <div className="flex items-center mb-2" data-id="element-4375">
                    <span className="px-2 py-1 bg-green-100 text-green-800 rounded font-semibold mr-3" data-id="element-4376">
                      POST
                    </span>
                    <span className="text-gray-700" data-id="element-4377">
                      /api/tenant/documents/upload
                    </span>
                  </div>
                  <p className="text-gray-500 text-xs ml-16" data-id="element-4378">
                    Upload verification documents
                  </p>
                </div>

                <div className="font-mono text-sm" data-id="element-4379">
                  <div className="flex items-center mb-2" data-id="element-4380">
                    <span className="px-2 py-1 bg-blue-100 text-blue-800 rounded font-semibold mr-3" data-id="element-4381">
                      GET
                    </span>
                    <span className="text-gray-700" data-id="element-4382">/api/tenant/payments</span>
                  </div>
                  <p className="text-gray-500 text-xs ml-16" data-id="element-4383">
                    Get payment history and schedule
                  </p>
                </div>

                <div className="font-mono text-sm" data-id="element-4384">
                  <div className="flex items-center mb-2" data-id="element-4385">
                    <span className="px-2 py-1 bg-blue-100 text-blue-800 rounded font-semibold mr-3" data-id="element-4386">
                      GET
                    </span>
                    <span className="text-gray-700" data-id="element-4387">
                      /api/tenant/risk-score
                    </span>
                  </div>
                  <p className="text-gray-500 text-xs ml-16" data-id="element-4388">
                    Get AI-calculated risk score
                  </p>
                </div>
              </div>
            </div>

            <div className="bg-yellow-50 border border-yellow-200 rounded-xl p-6" data-id="element-4389">
              <h3 className="text-sm font-semibold text-yellow-900 mb-2 flex items-center" data-id="element-4390">
                <AlertCircle className="h-4 w-4 mr-2" data-id="element-4391" />
                Authentication Required
              </h3>
              <p className="text-sm text-yellow-800" data-id="element-4392">
                All API endpoints require a valid JWT token in the Authorization
                header:
              </p>
              <code className="block mt-2 bg-yellow-100 p-2 rounded text-xs font-mono text-yellow-900" data-id="element-4393">
                Authorization: Bearer &lt;your_jwt_token&gt;
              </code>
            </div>
          </div>;
      default:
        return <div data-id="element-4394">Select a section to view documentation</div>;
    }
  };
  return <div className="flex min-h-screen bg-gray-50 pb-16 lg:pb-0" data-id="element-4395">
      <Sidebar data-id="element-4396" />

      <main className="flex-1 lg:ml-[240px] transition-all duration-300" data-id="element-4397">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6 lg:py-8" data-id="element-4398">
          <Breadcrumbs data-id="element-4399" />

          <motion.div initial={{
          opacity: 0,
          y: 20
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          duration: 0.5
        }} data-id="element-4400">
            <div className="mb-8" data-id="element-4401">
              <h1 className="text-3xl font-bold text-gray-900 flex items-center" data-id="element-4402">
                <Code className="h-8 w-8 text-blue-600 mr-3" data-id="element-4403" />
                Developer Documentation
              </h1>
              <p className="mt-2 text-gray-600" data-id="element-4404">
                Complete technical reference for the Renta platform
              </p>
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-4 gap-8" data-id="element-4405">
              {/* Sidebar Navigation */}
              <div className="lg:col-span-1" data-id="element-4406">
                <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-4 sticky top-8" data-id="element-4407">
                  <h3 className="text-sm font-semibold text-gray-900 mb-4 uppercase tracking-wide" data-id="element-4408">
                    Sections
                  </h3>
                  <nav className="space-y-1" data-id="element-4409">
                    {sections.map(section => <button key={section.id} onClick={() => setActiveSection(section.id)} className={`w-full flex items-center px-3 py-2 text-sm rounded-lg transition-colors ${activeSection === section.id ? 'bg-blue-50 text-blue-700 font-medium' : 'text-gray-600 hover:bg-gray-50 hover:text-gray-900'}`} data-id="element-4410">
                        <section.icon className="h-4 w-4 mr-3 flex-shrink-0" data-id="element-4411" />
                        <span className="truncate" data-id="element-4412">{section.title}</span>
                      </button>)}
                  </nav>
                </div>
              </div>

              {/* Main Content */}
              <div className="lg:col-span-3" data-id="element-4413">
                <motion.div key={activeSection} initial={{
                opacity: 0,
                x: 20
              }} animate={{
                opacity: 1,
                x: 0
              }} transition={{
                duration: 0.3
              }} data-id="element-4414">
                  {renderContent()}
                </motion.div>
              </div>
            </div>
          </motion.div>
        </div>
      </main>
    </div>;
}